import React, { useState } from 'react'
import { CVRParticipant } from '../../types'
import { participantRowKey } from '../../utils/participantKey'
import { Section } from '../ui'
import ParticipantRow, { StripExpandLink, StripHistoricDivider, StripSectionHeader } from './ParticipantRow'

/**
 * "Legale ejere" — entries from the CVR ejerregister. We follow the
 * same primary/secondary split used by datacvr.virk.dk: entries flagged
 * as `legalRegisterShowInPrimaryList === true` are the current open
 * ownership records, everything else (disposals, closed periods) hides
 * behind a toggle.
 */
interface LegalOwnersProps {
  owners: CVRParticipant[]
  embedded?: boolean
  layout?: 'default' | 'overview'
  stripHeader?: { title: string; suffix?: string | null }
}

const LegalOwners: React.FC<LegalOwnersProps> = ({
  owners,
  embedded = false,
  layout = 'default',
  stripHeader,
}) => {
  const [showRest, setShowRest] = useState(false)
  const overview = layout === 'overview'

  const primary = owners.filter((p) => p.role.legalRegisterShowInPrimaryList === true)
  const rest = owners.filter((p) => p.role.legalRegisterShowInPrimaryList !== true)

  const primaryBlock =
    owners.length === 0 ? (
      <p className={`text-ink-500 ${overview ? 'text-xs' : 'text-sm'}`}>
        {overview ? 'Ingen ejerregister-poster.' : 'Ingen poster i ejerregisteret i denne visning.'}
      </p>
    ) : primary.length === 0 ? (
      <p className={`text-ink-500 ${overview ? 'text-xs' : 'text-sm'}`}>
        {overview
          ? 'Ingen aktuelle legale ejere — se historiske.'
          : 'Ingen aktuelle åbne ejere i ejerregistret. Udvid listen nedenfor for tidligere poster.'}
      </p>
    ) : (
      <ul className={overview ? 'list-none space-y-0.5' : 'list-none'}>
        {primary.map((p, i) => (
          <ParticipantRow
            key={participantRowKey('lo-primary', p, i)}
            participant={p}
            variant={overview ? 'strip' : 'owner'}
          />
        ))}
      </ul>
    )

  const restBlock = showRest && rest.length > 0 && (
    <>
      {overview && <StripHistoricDivider />}
      <ul className={overview ? 'list-none space-y-0.5' : 'list-none mt-3 border-l-2 border-rule pl-4'}>
        {rest.map((p, i) => (
          <ParticipantRow
            key={participantRowKey('lo-rest', p, i)}
            participant={p}
            variant={overview ? 'strip' : 'owner'}
            forceHistoric
            stripExpanded={overview}
          />
        ))}
      </ul>
    </>
  )

  const restToggle = rest.length > 0 && (
    <StripExpandLink expanded={showRest} onToggle={() => setShowRest((v) => !v)}>
      {showRest ? 'Skjul' : 'Vis'}{' '}
      {overview ? 'historiske' : 'afhændede og øvrige poster'} ({rest.length})
    </StripExpandLink>
  )

  const body =
    overview && embedded ? (
      <>
        {stripHeader && (
          <StripSectionHeader
            title={stripHeader.title}
            suffix={stripHeader.suffix}
            action={restToggle}
          />
        )}
        {primaryBlock}
        {restBlock}
      </>
    ) : (
      <>
        {primaryBlock}
        {rest.length > 0 && (
          <div className={overview ? 'mt-1.5' : 'mt-3'}>
            <button
              type="button"
              onClick={() => setShowRest((v) => !v)}
              className={
                overview
                  ? 'text-[10px] text-ink-600 hover:text-ink underline underline-offset-2'
                  : 'text-xs text-ink-700 hover:text-ink underline underline-offset-4 decoration-rule-strong hover:decoration-signal'
              }
              aria-expanded={showRest}
            >
              {showRest ? 'Skjul' : 'Vis'}{' '}
              {overview ? 'øvrige i registeret' : 'afhændede og øvrige poster'} ({rest.length})
            </button>
            {restBlock}
          </div>
        )}
      </>
    )

  if (embedded) {
    return body
  }

  if (owners.length === 0) return null

  return (
    <Section title="Legale ejere" compact>
      {body}
    </Section>
  )
}

export default LegalOwners
